import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'What Does "Due Upon Receipt" Mean on an Invoice?',
      'description': 'Due upon receipt means payment is expected immediately when the client receives your invoice. Learn when to use it, how it compares to Net 30, and whether it is legally enforceable.',
      'url': 'https://www.invilic.com/blog/what-does-due-upon-receipt-mean',
      'datePublished': '2026-06-01',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What does "due upon receipt" mean on an invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': '"Due upon receipt" means the invoice is payable immediately when the client receives it — there is no grace period or set number of days. In practice, most clients interpret this as 1–3 business days rather than the exact moment the email arrives. It is the same as "due on receipt," "payable on receipt," and "payment due upon receipt."' },
        },
        {
          '@type': 'Question',
          'name': 'Is "due on receipt" the same as "due upon receipt"?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, they mean exactly the same thing. "Due on receipt," "due upon receipt," "payable on receipt," "payment due upon receipt," "balance due upon receipt," and "total due upon receipt" are all equivalent payment terms. There is no legal distinction between these phrasings — use whichever feels most natural for your business.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between "due upon receipt" and Net 30?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Net 30 gives your client 30 calendar days from the invoice date to pay. "Due upon receipt" means payment is expected immediately — no grace period. Due upon receipt is better for your cash flow but can create friction with corporate clients who process payments in monthly batches. Net 30 is the widely accepted B2B standard and suits most business-to-business relationships.' },
        },
        {
          '@type': 'Question',
          'name': 'Is "due upon receipt" legally enforceable?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, provided the payment terms were agreed before work began — in your contract, proposal, or email exchange. A court will not enforce payment terms the client was never told about. As long as your client was aware of and accepted "due upon receipt" terms, you can charge late fees and interest on any overdue balance. In the UK, statutory interest under the Late Payment of Commercial Debts Act also applies to overdue B2B invoices regardless of the specific term used.' },
        },
        {
          '@type': 'Question',
          'name': 'How many days is "due upon receipt" in practice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Legally, "due upon receipt" means the same day the invoice is received. In practice, most clients treat it as 1–5 business days — enough time to log the invoice, get approval if needed, and process the payment. If you need payment within a fixed window, specifying Net 3 or Net 7 gives a clearer, less ambiguous deadline.' },
        },
        {
          '@type': 'Question',
          'name': 'When should I use "due upon receipt" on my invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Use "due upon receipt" for new clients whose payment reliability you have not yet tested, small one-off jobs where delayed payment is not worth chasing for weeks, high-risk projects, or any work where cash flow is critical. Avoid it for large corporate clients with formal accounts payable departments — they process payments on fixed cycles and "due upon receipt" will not speed them up.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const synonyms = [
  'Due upon receipt',
  'Due on receipt',
  'Payable on receipt',
  'Payment due upon receipt',
  'Payment due on receipt',
  'Balance due upon receipt',
  'Total due upon receipt',
  'Amount due upon receipt',
  'Invoice due on receipt',
  'Due immediately upon receipt',
];

const comparison = [
  { term: 'Due upon Receipt', timeline: 'Same day (1–3 days in practice)', cashFlow: 'Best', friction: 'High with corporate clients', bestFor: 'New clients, small jobs, high-risk work' },
  { term: 'Net 7', timeline: '7 calendar days', cashFlow: 'Very good', friction: 'Moderate', bestFor: 'Freelancers, ongoing retainers' },
  { term: 'Net 15', timeline: '15 calendar days', cashFlow: 'Good', friction: 'Low to moderate', bestFor: 'Most freelancers and agencies' },
  { term: 'Net 30', timeline: '30 calendar days', cashFlow: 'Standard', friction: 'Very low', bestFor: 'B2B standard, corporate clients' },
  { term: 'Net 60', timeline: '60 calendar days', cashFlow: 'Poor', friction: 'None', bestFor: 'Large enterprises only' },
];

export default function DueUponReceiptPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title='What Does "Due Upon Receipt" Mean on an Invoice? | Invilic Blog'
        description='"Due upon receipt" means payment is expected immediately when the client receives your invoice. Covers all synonyms, vs Net 30, legal enforceability, and when to use it.'
        canonical="https://www.invilic.com/blog/what-does-due-upon-receipt-mean"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Basics</span>
          </div>
          <span className="inline-block bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Basics</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            What Does "Due Upon Receipt" Mean on an Invoice?
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 1, 2026 · 5 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            "Due upon receipt" is a payment term that means the client should pay your invoice as soon as they receive it — there is no set number of days as a grace period. It is the most direct payment term available and the fastest way to get paid, but it is not appropriate for every client or every situation.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What "Due Upon Receipt" Actually Means</h2>
          <p className="mb-4">
            Legally, "due upon receipt" means the invoice becomes payable the moment it is received. In practice, almost nobody pays within the same hour — nor do they expect to. Most clients treat it as a signal to pay within 1–5 business days, fast enough to clear from their accounts before the end of the week or month.
          </p>
          <p className="mb-4">
            The term does not specify a hard deadline date (unlike "Net 30," which gives a clear calendar date). This is both its strength and its weakness: it signals urgency without committing either party to an exact date, which can sometimes make it harder to enforce than a term like "Net 7."
          </p>
          <p className="mb-6">
            If you need a firm, unambiguous deadline, "Net 3" or "Net 7" is actually clearer — but "due upon receipt" remains widely used and universally understood.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">All the Ways to Say the Same Thing</h2>
          <p className="mb-4">Every phrase below means exactly the same thing as "due upon receipt." There is no legal distinction between any of them:</p>
          <div className="flex flex-wrap gap-2 mb-8">
            {synonyms.map(s => (
              <span key={s} className="text-sm bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-800 px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 font-mono">{s}</span>
            ))}
          </div>
          <p className="mb-6 text-sm">
            You will also see "balance due upon receipt" on invoices where a deposit was paid upfront — meaning the remaining balance is due immediately. It is the same term, just applied to the outstanding amount rather than the total.
          </p>

          <BlogCTA
            text="Add 'Due upon receipt' or any custom payment terms to your invoice in seconds. Free, no account needed."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Due Upon Receipt vs Net 30 — Which Should You Use?</h2>
          <p className="mb-5">
            The right choice depends on who you are invoicing and how important cash flow is to you right now.
          </p>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Term</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Payment Timeline</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Cash Flow</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Best For</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map(({ term, timeline, cashFlow, bestFor }) => (
                  <tr key={term} className={`border-b border-gray-100 dark:border-gray-800 ${term === 'Due upon Receipt' ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{term}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{timeline}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{cashFlow}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">When to Use "Due Upon Receipt"</h2>
          <ul className="space-y-3 mb-6 text-sm">
            {[
              { scenario: 'New or unknown clients', reason: "You don't yet know their payment habits. \"Due upon receipt\" sets a firm expectation from the start." },
              { scenario: 'Small one-off jobs', reason: "A £200 design job isn't worth chasing for 30 days. Prompt payment keeps it clean." },
              { scenario: 'High-risk projects', reason: "If you have any concern about non-payment, \"due upon receipt\" (combined with a deposit) is your strongest starting position." },
              { scenario: 'Cash flow crunch', reason: "When you have upcoming expenses, getting paid immediately matters more than client convenience." },
              { scenario: 'Retail and trade services', reason: 'Tradespeople, tutors, and service providers who deliver work in person commonly use "due upon receipt" or request payment at the time of service.' },
            ].map(({ scenario, reason }) => (
              <li key={scenario} className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                <span><strong className="font-semibold text-gray-900 dark:text-white">{scenario}:</strong> {reason}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">When NOT to Use "Due Upon Receipt"</h2>
          <ul className="space-y-3 mb-8 text-sm">
            {[
              { scenario: 'Corporate clients with AP departments', reason: "Large businesses process invoices in weekly or monthly batches. \"Due upon receipt\" will not change their payment cycle — it will just irritate their finance team." },
              { scenario: 'Long-term retainer relationships', reason: "Once you trust each other, Net 15 or Net 30 is more professional and relationship-friendly than an implied \"pay me now\" signal." },
              { scenario: 'High-value B2B contracts', reason: "For £10,000+ invoices with enterprise clients, \"due upon receipt\" can appear unprofessional. Net 30 is the expected norm." },
            ].map(({ scenario, reason }) => (
              <li key={scenario} className="flex items-start gap-3">
                <span className="text-red-500 font-bold flex-shrink-0">✗</span>
                <span><strong className="font-semibold text-gray-900 dark:text-white">{scenario}:</strong> {reason}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Is "Due Upon Receipt" Legally Enforceable?</h2>
          <p className="mb-4">
            Yes — provided your client was aware of and agreed to the payment terms before the work began. A payment term only becomes enforceable once it has been communicated and accepted. If you add "due upon receipt" to an invoice for the first time after work is complete, a client who disputes it may have grounds to claim they were not bound by the term.
          </p>
          <p className="mb-4">
            Best practice: include payment terms in your initial proposal, contract, or email quote — not only on the final invoice.
          </p>
          <p className="mb-6">
            Once enforceable, you can charge late fees and interest on any overdue balance. In the UK, the Late Payment of Commercial Debts Act allows statutory interest at 8% above the Bank of England base rate on B2B invoices, regardless of whether you explicitly stated an interest clause.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">How to Write "Due Upon Receipt" on an Invoice</h2>
          <p className="mb-4">Use the "Payment Terms" field on your invoice. Any of these phrasings is correct:</p>
          <div className="space-y-2 mb-8">
            {[
              '"Payment due upon receipt"',
              '"Due on receipt"',
              '"Payable immediately upon receipt"',
              '"Payment due upon receipt. Interest of 1.5% per month applies after 7 days."',
            ].map(ex => (
              <div key={ex} className="text-sm font-mono bg-gray-50 dark:bg-gray-900/30 px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800">
                {ex}
              </div>
            ))}
          </div>
          <p className="mb-8 text-sm">
            Adding an interest clause alongside the term — as in the last example — gives you a specific mechanism to enforce late payment, not just a vague obligation.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a professional invoice with 'Due upon receipt' or any custom payment terms. Free, no account needed."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/what-does-due-upon-receipt-mean" />
      </main>
    </div>
  );
}
